import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Layer, LayerType } from '../types';

interface Props {
  layers:     Layer[];
  longSidePx: number;   // rozmiar dłuższego boku eksportu (z ustawień jakości)
  onChange:   (layers: Layer[]) => void;
  onClose:    () => void;
}

// ── Helpers ──────────────────────────────────────────────────

let _idSeq = 0;
const genId = () => `lyr_${++_idSeq}_${Date.now()}`;

function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((res, rej) => {
    const r = new FileReader();
    r.onload  = e => res(e.target!.result as string);
    r.onerror = rej;
    r.readAsDataURL(file);
  });
}

function makeThumbnailAndSize(
  src: string,
  thumbSize = 64,
): Promise<{ thumb: string; w: number; h: number }> {
  return new Promise((res, rej) => {
    const img = new Image();
    img.onload = () => {
      const c = document.createElement('canvas');
      c.width = c.height = thumbSize;
      const ctx = c.getContext('2d')!;
      const s = Math.min(thumbSize / img.width, thumbSize / img.height);
      const dw = img.width * s, dh = img.height * s;
      ctx.drawImage(img, (thumbSize - dw) / 2, (thumbSize - dh) / 2, dw, dh);
      res({ thumb: c.toDataURL(), w: img.naturalWidth, h: img.naturalHeight });
    };
    img.onerror = rej;
    img.src = src;
  });
}

// ── Transform definitions ─────────────────────────────────────

type TKey = 'translateX' | 'translateY' | 'rotate' | 'scale';

interface TDef {
  key:     TKey;
  label:   string;
  min:     number;
  max:     number;
  step:    number;
  btnStep: number;
}

const TDEFS: TDef[] = [
  { key: 'translateX', label: 'Translate X', min: -300, max: 300, step: 1,    btnStep: 1    },
  { key: 'translateY', label: 'Translate Y', min: -300, max: 300, step: 1,    btnStep: 1    },
  { key: 'rotate',     label: 'Rotate',      min: -180, max: 180, step: 0.5,  btnStep: 1    },
  { key: 'scale',      label: 'Scale',       min: 0.05, max: 5.0, step: 0.01, btnStep: 0.05 },
];

/** Neutralna (domyślna) wartość każdej transformacji */
const NEUTRAL: Record<TKey, number> = {
  translateX: 0,
  translateY: 0,
  rotate:     0,
  scale:      1.0,
};

const TYPE_OPTS: { value: LayerType; label: string }[] = [
  { value: 'S', label: 'Shaped' },
  { value: 'F', label: 'Flat'   },
  { value: 'A', label: 'All'    },
];

const CANVAS_SIZE = 260;

// ── Component ────────────────────────────────────────────────

const LayersDialog: React.FC<Props> = ({ layers, longSidePx, onChange, onClose }) => {
  const [activeIdx,   setActiveIdx]   = useState<number | null>(
    layers.length > 0 ? 0 : null,
  );
  const [preciseMode, setPreciseMode] = useState(false);

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const fileRef   = useRef<HTMLInputElement>(null);
  const imgCache  = useRef<Map<string, HTMLImageElement>>(new Map());

  const active = (activeIdx !== null && activeIdx < layers.length)
    ? layers[activeIdx] : null;

  // ── Canvas drawing ────────────────────────────────────────

  const drawCanvas = useCallback(async () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const loadImg = (src: string) => {
      const cached = imgCache.current.get(src);
      if (cached) return Promise.resolve(cached);
      return new Promise<HTMLImageElement>((resolve, reject) => {
        const img = new Image();
        img.onload  = () => { imgCache.current.set(src, img); resolve(img); };
        img.onerror = () => reject(new Error('load error'));
        img.src = src;
      });
    };

    let imgs: HTMLImageElement[];
    try { imgs = await Promise.all(layers.map(l => loadImg(l.imageDataUrl))); }
    catch { return; }

    ctx.clearRect(0, 0, CANVAS_SIZE, CANVAS_SIZE);
    // Szachownica jako tło (sygnalizuje przezroczystość)
    for (let y = 0; y < CANVAS_SIZE; y += 16) {
      for (let x = 0; x < CANVAS_SIZE; x += 16) {
        ctx.fillStyle = ((x + y) / 16) % 2 === 0 ? '#5a5a5a' : '#4a4a4a';
        ctx.fillRect(x, y, 16, 16);
      }
    }

    const cx = CANVAS_SIZE / 2, cy = CANVAS_SIZE / 2;
    // Rysuj od dołu stosu (ostatni indeks) do góry (indeks 0)
    for (let i = layers.length - 1; i >= 0; i--) {
      const l   = layers[i];
      const img = imgs[i];
      ctx.save();
      ctx.translate(cx + l.translateX, cy + l.translateY);
      ctx.rotate((l.rotate * Math.PI) / 180);
      const dw = img.naturalWidth  * l.baseScale * l.scale;
      const dh = img.naturalHeight * l.baseScale * l.scale;
      ctx.drawImage(img, -dw / 2, -dh / 2, dw, dh);
      ctx.restore();
    }
  }, [layers]);

  useEffect(() => { drawCanvas(); }, [drawCanvas]);

  // ── Helpers mutacji ───────────────────────────────────────

  const updateLayer = (idx: number, patch: Partial<Layer>) => {
    const next = [...layers];
    next[idx] = { ...next[idx], ...patch };
    onChange(next);
  };

  const moveLayer = (idx: number, dir: -1 | 1) => {
    const ni = idx + dir;
    if (ni < 0 || ni >= layers.length) return;
    const next = [...layers];
    [next[idx], next[ni]] = [next[ni], next[idx]];
    onChange(next);
    setActiveIdx(ni);
  };

  // ── Event handlers ────────────────────────────────────────

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files?.length) return;
    const added: Layer[] = [];
    for (const file of Array.from(files)) {
      const dataUrl               = await readFileAsDataUrl(file);
      const { thumb, w, h }       = await makeThumbnailAndSize(dataUrl);
      const baseScale             = Math.min(CANVAS_SIZE / w, CANVAS_SIZE / h);
      added.push({
        id: genId(), fileName: file.name,
        imageDataUrl: dataUrl, thumbnailDataUrl: thumb,
        type: 'S', translateX: 0, translateY: 0, rotate: 0,
        baseScale, scale: 1.0,
      });
    }
    onChange([...added, ...layers]);  // nowe na górze stosu
    setActiveIdx(0);
    e.target.value = '';
  };

  const handleDeletePic = () => {
    if (activeIdx === null) return;
    const next = layers.filter((_, i) => i !== activeIdx);
    onChange(next);
    setActiveIdx(next.length > 0 ? Math.min(activeIdx, next.length - 1) : null);
  };

  const handleSaveAsPng = async () => {
    if (layers.length === 0) return;

    const imgs = await Promise.all(
      layers.map(l => new Promise<HTMLImageElement>((resolve, reject) => {
        const img = new Image();
        img.onload  = () => resolve(img);
        img.onerror = reject;
        img.src = l.imageDataUrl;
      })),
    );

    const size      = longSidePx;
    const drawScale = size / CANVAS_SIZE;
    const cx        = size / 2;
    const cy        = size / 2;

    const off  = document.createElement('canvas');
    off.width  = size;
    off.height = size;
    const ctx  = off.getContext('2d')!;

    for (let i = layers.length - 1; i >= 0; i--) {
      const l   = layers[i];
      const img = imgs[i];
      ctx.save();
      ctx.translate(cx + l.translateX * drawScale, cy + l.translateY * drawScale);
      ctx.rotate((l.rotate * Math.PI) / 180);
      const dw = img.naturalWidth  * l.baseScale * l.scale * drawScale;
      const dh = img.naturalHeight * l.baseScale * l.scale * drawScale;
      ctx.drawImage(img, -dw / 2, -dh / 2, dw, dh);
      ctx.restore();
    }

    const a = document.createElement('a');
    a.download = 'composite.png';
    a.href = off.toDataURL('image/png');
    a.click();
  };

  const handleTransform = (key: TKey, raw: number) => {
    if (activeIdx === null) return;
    const def = TDEFS.find(d => d.key === key)!;
    updateLayer(activeIdx, { [key]: Math.max(def.min, Math.min(def.max, raw)) });
  };

  const handleStep = (key: TKey, dir: -1 | 1) => {
    if (!active) return;
    const def = TDEFS.find(d => d.key === key)!;
    handleTransform(key, (active[key] as number) + dir * def.btnStep);
  };

  const handleReset = (key: TKey) => {
    if (activeIdx === null) return;
    updateLayer(activeIdx, { [key]: NEUTRAL[key] });
  };

  // ── Render transformacji (wspólny dla obu trybów) ─────────

  const renderTransformRows = () => TDEFS.map(def => {
    const val     = active ? (active[def.key] as number) : 0;
    const lbl     = def.key === 'scale' ? `Scale ${val.toFixed(2)}` : def.label;
    const isNeutral = val === NEUTRAL[def.key];
    return (
      <div key={def.key} className="tr-row">
        <span className="tr-label">{lbl}</span>
        <div className="tr-controls">
          <button className="btn-pm" disabled={!active}
            onClick={() => handleStep(def.key, -1)}>−</button>
          <input type="range" className="tr-slider"
            min={def.min} max={def.max} step={def.step}
            value={val} disabled={!active}
            onChange={e => handleTransform(def.key, +e.target.value)} />
          <button className="btn-pm" disabled={!active}
            onClick={() => handleStep(def.key, 1)}>+</button>
          <button
            className="btn-reset"
            disabled={!active || isNeutral}
            title={`Reset to ${NEUTRAL[def.key]}`}
            onClick={() => handleReset(def.key)}
          >↺</button>
          <input type="number" className="tr-num"
            value={val.toFixed(2)} disabled={!active} step={def.step}
            onChange={e => handleTransform(def.key, +e.target.value)} />
        </div>
      </div>
    );
  });

  const renderTypeRow = () => (
    <div className="tr-row">
      <div className="tr-controls">
        <select className="type-select" disabled={!active}
          value={active?.type ?? 'S'}
          onChange={e =>
            activeIdx !== null &&
            updateLayer(activeIdx, { type: e.target.value as LayerType })
          }>
          {TYPE_OPTS.map(o =>
            <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </div>
    </div>
  );

  // ── Tryb precyzyjnych transformacji ──────────────────────
  // Panel nakłada się na lewy panel głównego okna (≈390px).
  // Tło overlay jest przezroczyste i pointer-events: none,
  // dzięki czemu viewport 3D pozostaje w pełni dostępny.

  if (preciseMode) {
    return (
      <div className="layers-overlay precise-mode">
        <div className="precise-panel">

          <div className="precise-header">
            <button
              className="btn-precise-exit"
              onClick={() => setPreciseMode(false)}
              title="Wróć do okna Layers"
            >← Layers</button>
            <span className="precise-title">
              {active ? active.fileName : '(brak warstwy)'}
            </span>
          </div>

          <div className="precise-canvas-wrap">
            <canvas
              ref={canvasRef}
              width={CANVAS_SIZE}
              height={CANVAS_SIZE}
              className="layers-canvas"
            />
          </div>

          <div className="precise-transforms">
            {renderTransformRows()}
            {renderTypeRow()}

            <div className="tr-precise-btn-row">
              <button
                className="btn-precise-enter"
                onClick={onClose}
                title="Zamknij okno Layers"
              >
                ✕ Close Precise Mode
              </button>
            </div>
          </div>

        </div>
        <input ref={fileRef} type="file" accept="image/*" multiple
          style={{ display: 'none' }} onChange={handleFileChange} />
      </div>
    );
  }

  // ── Tryb normalny ─────────────────────────────────────────

  return (
    <div className="layers-overlay">
      <div className="layers-dialog">

        {/* ── Lewa kolumna: przyciski ── */}
        <div className="layers-left">
          <button onClick={() => fileRef.current?.click()}>Load Pic</button>
          <button onClick={handleDeletePic} disabled={activeIdx === null}>Delete Pic</button>
          <button
            onClick={() => activeIdx !== null && moveLayer(activeIdx, -1)}
            disabled={activeIdx === null || activeIdx === 0}>
            Move Up
          </button>
          <button
            onClick={() => activeIdx !== null && moveLayer(activeIdx, 1)}
            disabled={activeIdx === null || activeIdx === layers.length - 1}>
            Move Down
          </button>
          <button onClick={handleSaveAsPng}>Save as PNG</button>
          <div className="lyr-spacer" />
          <button onClick={onClose}>Close</button>

          <input ref={fileRef} type="file" accept="image/*" multiple
            style={{ display: 'none' }} onChange={handleFileChange} />
        </div>

        {/* ── Środkowa kolumna: podgląd + transformacje ── */}
        <div className="layers-center">
          <canvas
            ref={canvasRef}
            width={CANVAS_SIZE}
            height={CANVAS_SIZE}
            className="layers-canvas"
          />

          <div className="layers-transforms">
            {renderTransformRows()}
            {renderTypeRow()}

            <div className="tr-precise-btn-row">
              <button
                className="btn-precise-enter"
                disabled={!active}
                onClick={() => setPreciseMode(true)}
                title="Przełącz na tryb precyzyjnych transformacji (pełna szerokość)"
              >
                ⤢ Precise Transform Mode
              </button>
            </div>
          </div>
        </div>

        {/* ── Prawa kolumna: lista warstw ── */}
        <div className="layers-right">
          <div className="lyr-list-head">
            <span className="lc-thumb">Thumbnail</span>
            <span className="lc-type">Type</span>
            <span className="lc-fname">File Name</span>
            <span className="lc-act">Actions</span>
          </div>
          <div className="lyr-list-body">
            {layers.map((layer, idx) => (
              <div
                key={layer.id}
                className={`lyr-row ${idx === activeIdx ? 'lyr-active' : ''}`}
                onClick={() => setActiveIdx(idx)}
              >
                <span className="lc-thumb">
                  <img src={layer.thumbnailDataUrl} alt="" className="lyr-thumb-img" />
                </span>
                <span className="lc-type">{layer.type}</span>
                <span className="lc-fname" title={layer.fileName}>
                  {layer.fileName}
                </span>
                <span className="lc-act">
                  <button className="btn-arr" disabled={idx === 0}
                    onClick={e => { e.stopPropagation(); moveLayer(idx, -1); }}>↑</button>
                  <button className="btn-arr" disabled={idx === layers.length - 1}
                    onClick={e => { e.stopPropagation(); moveLayer(idx, 1); }}>↓</button>
                </span>
              </div>
            ))}
          </div>
        </div>

      </div>
    </div>
  );
};

export default LayersDialog;
